import { createContext, useContext, useEffect, useMemo, useState } from "react";

const AiModeContext = createContext(null);
const STORAGE_KEY = "aethercv-ai-mode";
const STYLE_STORAGE_KEY = "aethercv-writing-style";
const STYLE_MIGRATION_KEY = "aethercv-writing-style-migrated";
const ALLOWED_STYLES = new Set(["human", "professional", "concise", "narrative"]);

export function AiModeProvider({ children }) {
  const [mode, setMode] = useState("fast");
  const [writingStyle, setWritingStyle] = useState("human");

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "fast" || saved === "best") {
      setMode(saved);
    }
    const savedStyle = localStorage.getItem(STYLE_STORAGE_KEY);
    const migrated = localStorage.getItem(STYLE_MIGRATION_KEY);
    if (!migrated) {
      setWritingStyle("human");
      localStorage.setItem(STYLE_STORAGE_KEY, "human");
      localStorage.setItem(STYLE_MIGRATION_KEY, "true");
      return;
    }
    if (savedStyle && ALLOWED_STYLES.has(savedStyle)) {
      setWritingStyle(savedStyle);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (ALLOWED_STYLES.has(writingStyle)) {
      localStorage.setItem(STYLE_STORAGE_KEY, writingStyle);
    }
  }, [writingStyle]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      isBest: mode === "best",
      writingStyle,
      setWritingStyle,
    }),
    [mode, writingStyle]
  );

  return <AiModeContext.Provider value={value}>{children}</AiModeContext.Provider>;
}

export function useAiMode() {
  const ctx = useContext(AiModeContext);
  if (!ctx) throw new Error("useAiMode must be used within AiModeProvider");
  return ctx;
}
